import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, Modal, Typography, CircularProgress, Button } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import { logoutAction } from "../../../redux/actions/authAction";
import {
  connectStoreAutoCloseSocket,
  StoreAutoCloseCallback,
} from "../../../util/storeAutoCloseSocket";
import { Colors } from "../../../configs";
import { IStoreAutoCloseStatusMessage } from "../../../typings/interfaces/responses/store-auto-close-message";
import { pxToRem } from "../../../util";

const modalWrapperStyles = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: Colors.WHITE,
  borderRadius: pxToRem(10),
  width: pxToRem(448),
  padding: `${pxToRem(40)} ${pxToRem(36)}`,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  gap: pxToRem(24),
  boxShadow: "0px 16px 40px rgba(0, 0, 0, 0.12)",
};

const AutoCloseListener: FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [modalOpen, setModalOpen] = useState(false);
  const [status, setStatus] = useState<IStoreAutoCloseStatusMessage | null>(null);
  const clientRef = useRef<ReturnType<typeof connectStoreAutoCloseSocket> | null>(
    null
  );
  const logoutScheduledRef = useRef(false);

  const storeId = useMemo(() => user?.storeId, [user?.storeId]);

  useEffect(() => {
    if (!storeId) {
      setModalOpen(false);
      setStatus(null);
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
      return;
    }

    const onMessage: StoreAutoCloseCallback = (payload) => {
      const normalizedStatus = payload.status?.toUpperCase?.() ?? "";
      setStatus(payload);

      if (normalizedStatus === "STARTED") {
        setModalOpen(true);
        logoutScheduledRef.current = false;
      } else if (normalizedStatus === "COMPLETED") {
        setModalOpen(true);
        if (!logoutScheduledRef.current) {
          logoutScheduledRef.current = true;
          setTimeout(() => {
            dispatch(logoutAction());
          }, 1500);
        }
      } else if (normalizedStatus === "FAILED") {
        setModalOpen(true);
      }
    };

    clientRef.current = connectStoreAutoCloseSocket(storeId, onMessage);

    return () => {
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
      setModalOpen(false);
      setStatus(null);
      logoutScheduledRef.current = false;
    };
  }, [dispatch, storeId]);

  const handleDismiss = () => {
    if (status?.status?.toUpperCase() === "FAILED") {
      setModalOpen(false);
      setStatus(null);
    }
  };

  if (!modalOpen) {
    return null;
  }

  const statusKey = status?.status?.toUpperCase?.() ?? "STARTED";
  const showDismiss = statusKey === "FAILED";

  const message =
    status?.message ??
    (statusKey === "STARTED"
      ? "Auto closing the store. Please wait..."
      : statusKey === "COMPLETED"
      ? "Store auto-close completed. Signing you out..."
      : "Auto close failed. Please contact your supervisor.");

  return (
    <Modal
      open={modalOpen}
      onClose={() => {}}
      hideBackdrop={false}
      disableAutoFocus
      disableEnforceFocus
      sx={{
        zIndex: 2000,
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.65)",
        },
      }}
    >
      <Box sx={modalWrapperStyles}>
        {statusKey === "FAILED" ? (
          <Typography
            component="span"
            sx={{ fontSize: "3rem", color: Colors.ERROR_RED, fontWeight: "bold" }}
          >
            !
          </Typography>
        ) : (
          <CircularProgress size={pxToRem(56)} sx={{ color: Colors.PRIMARY }} />
        )}
        <Typography
          variant="h5"
          sx={{
            color: Colors.TEXT_GRAY_DARK,
            fontWeight: 700,
            textAlign: "center",
            fontSize: pxToRem(26),
          }}
        >
          {statusKey === "STARTED"
            ? "Auto Close In Progress"
            : statusKey === "COMPLETED"
            ? "Store Auto Closed"
            : "Auto Close Failed"}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: Colors.MAIN_GRAY,
            textAlign: "center",
            lineHeight: 1.6,
            fontSize: pxToRem(18),
          }}
        >
          {message}
        </Typography>
        {showDismiss && (
          <Button
            variant="contained"
            onClick={handleDismiss}
            sx={{
              backgroundColor: Colors.PRIMARY,
              color: Colors.WHITE,
              paddingX: pxToRem(36),
              paddingY: pxToRem(12),
              textTransform: "none",
              fontWeight: 600,
              borderRadius: pxToRem(8),
              fontSize: pxToRem(18),
            }}
          >
            Dismiss
          </Button>
        )}
      </Box>
    </Modal>
  );
};

export default AutoCloseListener;

